import { FC } from 'react';
import {
  serializationOptionsForBooleanArrayValue,
  serializationOptionsForStringArrayValue,
  serializationOptionsForStringValue,
  useSearchParamState,
} from 'utils/hooks/useSearchParamState';
import { ensureEnum } from 'utils/tsHelpers';

import { SubscriptionPolicy } from '../../domain';
import { QueryParam } from '../constants';

import { FiltersContext } from './FiltersContext';

export const FiltersContextProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [search, setSearch] = useSearchParamState({
    searchParamKey: QueryParam.search,
    stateValueWhenSearchParamIsUnset: '',
    ...serializationOptionsForStringValue,
  });

  const [policyKeys, setPolicyKeys] = useSearchParamState({
    searchParamKey: QueryParam.policy,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const subscriptionPolicies = policyKeys.map((key) =>
    ensureEnum(key, SubscriptionPolicy, SubscriptionPolicy.optIn),
  );
  const toggleSubscriptionPolicy = (policy: SubscriptionPolicy) => {
    if (subscriptionPolicies.includes(policy)) {
      setPolicyKeys(policyKeys.filter((key) => key !== policy));
    } else {
      setPolicyKeys([...policyKeys, policy]);
    }
  };

  const [subscriptionStatuses, setSubscriptionStatuses] = useSearchParamState({
    searchParamKey: QueryParam.status,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForBooleanArrayValue,
  });
  const toggleSubscriptionStatus = (status: boolean) => {
    if (subscriptionStatuses.includes(status)) {
      setSubscriptionStatuses(subscriptionStatuses.filter((key) => key !== status));
    } else {
      setSubscriptionStatuses([...subscriptionStatuses, status]);
    }
  };

  const [mediums, setMediums] = useSearchParamState({
    searchParamKey: QueryParam.medium,
    stateValueWhenSearchParamIsUnset: [],
    ...serializationOptionsForStringArrayValue,
  });
  const toggleMedium = (medium: string) => {
    if (mediums.includes(medium)) {
      setMediums(mediums.filter((key) => key !== medium));
    } else {
      setMediums([...mediums, medium]);
    }
  };

  const canClearFilters = !!policyKeys.length || !!subscriptionStatuses.length || !!mediums.length;
  const clearFilters = canClearFilters
    ? () => {
        setPolicyKeys([]);
        setSubscriptionStatuses([]);
        setMediums([]);
      }
    : null;

  return (
    <FiltersContext.Provider
      value={{
        search,
        setSearch,
        subscriptionPolicies,
        toggleSubscriptionPolicy,
        subscriptionStatuses,
        toggleSubscriptionStatus,
        mediums,
        toggleMedium,
        clearFilters,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};
